import Vimeo from '@u-wave/react-vimeo'; // https://www.npmjs.com/package/@u-wave/react-vimeo
import { useEffect, useMemo, useState } from 'react';
import useVimeo from '../../hooks/useVimeo';
import { Duration } from 'luxon';
import { captureException } from '@sentry/nextjs';
import clsx from 'clsx';

interface VimeoPlayerProps {
  videoUrl: string;
  loop?: boolean;
  muted?: boolean;
  controls?: boolean;
  showVideoRuntime?: boolean;
  videoAutoPlay?: boolean;
  containerClasses?: string;
  pause?: boolean;
  onReady?: () => void;
  fitByWidth?: boolean;
}

const VimeoPlayer = ({
  videoUrl,
  loop = true,
  muted = false,
  controls = false,
  showVideoRuntime = true,
  videoAutoPlay = false,
  containerClasses,
  pause,
  onReady,
  fitByWidth = true,
}: VimeoPlayerProps) => {
  const [error, setError] = useState<string>();
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errorCopy] = useState('Video error');
  const { getVimeoVideoDetails } = useVimeo();
  const [videoDuration, setVideoDuration] = useState(null);
  const [urlHasChanged, setUrlHasChanged] = useState(false);

  useEffect(() => {
    setUrlHasChanged(true);
    const getVideoDetails = async () => {
      try {
        const videoId = videoUrl.split('/').pop();
        const res = await getVimeoVideoDetails(videoId);
        const duration = Duration.fromObject({
          seconds: res.duration,
        }).toFormat('mm:ss');
        setVideoDuration(duration);
        setUrlHasChanged(false);
        setError(undefined);
      } catch (error) {
        captureException(error);
      }
    };

    if (videoUrl) {
      getVideoDetails();
    }

    if (videoAutoPlay) {
      setPlaying(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoUrl]);

  const handleError = (error) => {
    //handle error silently
  };

  const shouldPasue = useMemo(() => {
    if (pause !== undefined) {
      return pause;
    } else {
      return controls ? undefined : !playing;
    }
  }, [pause, controls, playing]);

  if (urlHasChanged) {
    return null;
  }

  return (
    <>
      {error && <div>{errorCopy}</div>}

      {!error && (
        <div
          className={clsx(['overflow-hidden relative w-full h-full group', containerClasses])}
          onMouseEnter={() => {
            setPlaying(true);
          }}
          onMouseLeave={() => {
            setPlaying(false);
          }}
        >
          {!controls && showVideoRuntime && !playing && (
            <div className="bg-white absolute top-[16px] left-[16px] pointer-events-none z-10 rounded-full flex items-center px-[14px] py-[5px] group-hover:opacity-0">
              <svg
                width="11"
                height="14"
                viewBox="0 0 11 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="mr-[7px]"
              >
                <path
                  d="M10.5 6.13387C11.1667 6.51877 11.1667 7.48102 10.5 7.86592L1.5 13.0621C0.833332 13.447 -5.31688e-07 12.9658 -4.98039e-07 12.196L-4.37766e-08 1.80374C-1.01275e-08 1.03394 0.833333 0.552812 1.5 0.937712L10.5 6.13387Z"
                  fill="#FF3E00"
                />
              </svg>
              <p className="font-nhg uppercase leading-[1] tracking-[0.15em] relative inline-block text-sm 3xl:text-[15px] 4xl:text-[16px] text-black !text-[11px] font-normal">
                {videoDuration}
              </p>
            </div>
          )}

          <div
            className={clsx([
              'transition-all',
              controls ? '' : 'pointer-events-none',
              loading ? 'opacity-0' : ' opacity-100',
            ])}
          >
            <Vimeo
              video={videoUrl}
              autoplay={videoAutoPlay}
              muted={muted}
              onError={(error) => handleError(error)}
              loop={loop}
              responsive={true}
              dnt={true}
              controls={controls}
              showByline={false}
              showPortrait={false}
              showTitle={false}
              paused={shouldPasue}
              className={clsx([fitByWidth ? 'vimeo-player-wrap' : '', controls ? 'controls' : ''])}
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%,-50%)',
                aspectRatio: fitByWidth ? '16 / 9' : undefined,
                boxSizing: 'border-box',
                minHeight: 'calc(100% + 2px)',
                minWidth: 'calc(100% + 2px)',
              }}
              onLoaded={() => {
                setLoading(false);
                if (onReady) {
                  onReady();
                }
              }}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default VimeoPlayer;
